import { computed, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';

import { RoleType, UserProfileType } from '@type/user.type';
import { filter } from 'rxjs/operators';

import { AuthenticationService } from '@services/api/authentication.service';
import { UserService } from '@services/api/user.service';
import { TokenService } from '@services/helper/token.service';
import { UserPermissionService } from '@services/helper/user-permission.service';

/**
 * Centralized state service for the user profile section.
 *
 * Responsibilities:
 *  - Derive userProfileId from the current route ('own' | 'add' | '<id>')
 *  - Fire the correct API query based on route
 *  - Re-load profile data on every route change automatically
 *  - Expose profileData, resolvedUserId, and permission-derived flags
 *    that every sub-component can inject instead of duplicating the logic
 *
 * Hierarchy (high → low):
 *   admin/developer > master agency manager > agency manager > agent
 */
@Injectable({ providedIn: 'root' })
export class UserProfileStateService {
  private readonly authService = inject(AuthenticationService);
  private readonly userService = inject(UserService);
  private readonly userPermission = inject(UserPermissionService);
  private readonly tokenService = inject(TokenService);
  private readonly router = inject(Router);

  // ─── Selected role in the Roles & Permissions form ────────────
  // Writable signal — set by RolePermissionsComponent when the user
  // changes the role dropdown. Falls back to the saved role from API.
  private readonly _selectedRoleNameOverride = signal<string | null>(null);

  /** The role name currently selected in the form (or the saved role if none chosen yet) */
  public readonly selectedRoleName = computed(
    (): string => this._selectedRoleNameOverride() ?? this.targetRoleName(),
  );

  /** Called by RolePermissionsComponent when the role dropdown changes */
  public setSelectedRoleName(name: string | null): void {
    this._selectedRoleNameOverride.set(name);
  }

  /** Reset override when navigating away or saving */
  public clearSelectedRoleOverride(): void {
    this._selectedRoleNameOverride.set(null);
  }

  // ─── Track navigation so we re-evaluate on every route change ───
  private readonly _navEnd = toSignal(
    this.router.events.pipe(filter((e) => e instanceof NavigationEnd)),
    { initialValue: null },
  );

  // ─── Derived profile mode from URL ──────────────────────────────
  // Reads the live URL on every NavigationEnd so switching from
  // /my-preferences to /users-dev/34 instantly updates everything.
  public readonly userProfileId = computed((): 'add' | 'own' | string => {
    void this._navEnd(); // subscribe to nav changes
    const url = this.router.url;
    if (url.includes('my-preferences')) return 'own';
    // Extract numeric/add id from /users-dev/:id or /users/:id
    const match = url.match(/\/users(?:-dev)?\/([^/]+)/);
    return match?.[1] ?? 'add';
  });

  // ─── GET /api/me ─────────────────────────────────────────────────
  public readonly ownProfileQuery = this.authService.getAuthenticatedUserQuery({
    enabled: computed(() => this.userProfileId() === 'own'),
    select: (response: any) => {
      const { status, data } = response ?? {};
      return status && data ? (data as UserProfileType) : null;
    },
  });

  public readonly allRolesQuery = this.userService.queryGetAllUsersRoles({
    // This root-provided service is constructed by the app shell/header, so the
    // roles query would otherwise fire on public pages (e.g. the password-reset
    // link) while logged out — the 401 then trips the interceptor and bounces
    // the user to /auth/login. Only fetch roles once authenticated.
    enabled: computed(() => this.tokenService.isTokenValid()),
    select: (response: any) => {
      // `/roles?per_page=1000` returns a paginated envelope
      // ({ status, data: { data: [...] } }), but some environments return a
      // flat array under `data`. Unwrap both so the Role dropdown is populated
      // instead of showing empty.
      const raw = response?.data;
      const list = Array.isArray(raw) ? raw : (raw?.data ?? []);
      return Array.isArray(list) ? (list as RoleType[]) : [];
    },
  });

  // ─── GET /api/users/:id ──────────────────────────────────────────
  public readonly userByIdQuery = this.userService.queryGetUserById(
    () => Number(this.userProfileId()),
    {
      enabled: computed(() => {
        const id = this.userProfileId();
        // Must be a numeric string — not 'add', 'own', or any non-numeric value
        return id !== 'add' && id !== 'own' && !isNaN(Number(id)) && Number(id) > 0;
      }),
      select: (response: any) => {
        const { status, data } = response ?? {};
        return status && data ? (data as UserProfileType) : null;
      },
    },
  );

  // ─── Unified profile data — reactive to route ────────────────────
  public readonly profileData = computed<UserProfileType | null>(() => {
    const id = this.userProfileId();
    // Add mode must never "inherit" stale cached userByIdQuery data
    // (query is disabled but TanStack may retain last data in memory).
    if (id === 'add') return null;
    return id === 'own' ? this.ownProfileQuery.data() : this.userByIdQuery.data();
  });

  // ─── Numeric user ID (0 when add mode or not yet loaded) ────────
  public readonly resolvedUserId = computed((): number => {
    const id = this.userProfileId();
    if (id === 'add') return 0;
    if (id === 'own') return this.profileData()?.id ?? 0;
    return Number(id);
  });

  // ─── Target profile role name ────────────────────────────────────
  public readonly targetRoleName = computed(
    (): string => this.profileData()?.role?.name?.toLowerCase() ?? '',
  );

  // ─── Logged-in user role helpers (delegates to UserPermissionService) ─
  public readonly viewer = {
    isDeveloper: this.userPermission.isUserDeveloper,
    isAgencyManager: this.userPermission.isUserAgencyManager,
    isMasterAgencyManager: this.userPermission.isMasterAgencyManager,
    isAgent: this.userPermission.isUserAgent,
  };

  // ─── Hierarchy rank helpers ──────────────────────────────────────
  // Higher number = higher rank. Used to enforce "junior can't touch senior".
  // Developer(4) > Master Agent / Master Agency Manager(3) > Agency Manager(2) > Agent(1)
  // Public so component-level role-list filters can use it (equal-or-below assignment rule).
  public rankOf(roleName: string): number {
    switch (roleName) {
      case 'developer':
        return 4;
      case 'developer viewer':
        return 4;
      case 'master agent':
        return 3;
      case 'master agency manager':
        return 3;
      case 'agency manager':
        return 2;
      case 'agent':
        return 1;
      default:
        return 0;
    }
  }

  private readonly viewerRankName = computed((): string => {
    if (this.userPermission.isUserDeveloper()) return 'developer';
    if (this.userPermission.isUserDeveloperViewer()) return 'developer viewer';
    if (this.userPermission.isUserMasterAgent()) return 'master agent';
    if (this.userPermission.isMasterAgencyManager()) return 'master agency manager';
    if (this.userPermission.isUserAgencyManager()) return 'agency manager';
    if (this.userPermission.isUserAgent()) return 'agent';
    return '';
  });

  public readonly viewerRank = computed(() => this.rankOf(this.viewerRankName()));
  public readonly targetRank = computed(() => this.rankOf(this.targetRoleName()));

  // ─── Can the viewer SEE this profile at all? ────────────────────
  // Rule: equal-or-higher rank can view. Agent can only view their own profile.
  // Add / own modes always permitted.
  public readonly canViewProfile = computed((): boolean => {
    const id = this.userProfileId();
    if (id === 'own' || id === 'add') return true;
    // Agents are restricted to their own profile only
    if (this.userPermission.isUserAgent()) return false;
    // Target must be at the same or lower rank as the viewer
    return this.viewerRank() >= this.targetRank();
  });

  // ─── Can the viewer EDIT fields on this profile? ────────────────
  // Same rule as view: equal-or-below rank, agent only on own.
  public readonly canEditProfile = computed((): boolean => {
    if (!this.canViewProfile()) return false;
    const id = this.userProfileId();
    if (this.userPermission.isUserAgent()) return id === 'own';
    return true;
  });

  // ─── Can the viewer edit the EMAIL field? ───────────────────────
  // No one can change their own email. Email is read-only on own profile.
  public readonly canEditEmail = computed((): boolean => {
    return this.userProfileId() !== 'own';
  });

  // ─── Can the viewer edit the ROLE field? ────────────────────────
  // Rule:
  //  • No one can change their OWN role/permissions.
  //  • Agents can never manage roles.
  //  • Otherwise: viewer can edit role of any target whose rank is equal or below.
  public readonly canEditRole = computed((): boolean => {
    if (this.userProfileId() === 'own') return false;
    if (this.userPermission.isUserAgent()) return false;
    // FEC-1258 (Hina, 7 Oct): "The role field is still appearing" — an Agency Manager never
    // changes a role or an agency: their users are always Agents of their own agency, and
    // the Admin assigns the one AM per agency. The tab is read-only for them.
    if (this.userPermission.isUserAgencyManager()) return false;
    return this.viewerRank() >= this.targetRank();
  });

  // ─── Should the Roles & Permissions TAB be shown? ───────────────
  // Hidden for:
  //  • add mode (role is set in personal details)
  //  • own profile (no one edits their own role)
  //  • target user is_admin (admin role is system-locked)
  //  • agent viewer (can't manage roles at all)
  //  • viewer rank lower than target rank (junior cannot touch senior)
  public readonly showPermissionsTab = computed((): boolean => {
    const id = this.userProfileId();
    if (id === 'add' || id === 'own') return false;
    if (this.profileData()?.is_admin) return false;
    if (this.userPermission.isUserAgent()) return false;
    return this.viewerRank() >= this.targetRank();
  });

  // ─── Mandatory fields for progress bar ──────────────────────────
  /**
   * Base fields required for every user.
   * `company_id` is added on top only for roles that belong to an agency
   * (Agency Manager / Agent) — developers, master agents, master agency
   * managers don't sit under a company so requiring it would cap their
   * progress.
   *
   * FEC-1013: `mobile` counts toward completion — a profile is not "complete"
   * until a mobile number is provided. The contact preference (Email / SMS) is
   * handled separately in `profileProgress` as one extra slot, because it is a
   * pair of booleans, not a single presence-checked field.
   */
  private readonly baseMandatoryFields: (keyof UserProfileType)[] = [
    'first_name',
    'last_name',
    'display_name',
    'email',
    'mobile',
  ];

  public readonly mandatoryFields = computed<(keyof UserProfileType)[]>(() => {
    const role = this.targetRoleName();
    const needsCompany = role === 'agency manager' || role === 'agent';
    return needsCompany ? [...this.baseMandatoryFields, 'company_id'] : this.baseMandatoryFields;
  });

  public readonly profileProgress = computed((): number => {
    const data = this.profileData();
    if (!data) return 0;
    const fields = this.mandatoryFields();
    const completed = fields.filter((field) => {
      const value = data[field];
      if (typeof value === 'boolean') return true;
      if (typeof value === 'number') return value !== null && value !== undefined;
      return value !== null && value !== undefined && String(value).trim() !== '';
    });

    // FEC-1013: the contact preference (Email / SMS opt-in) is one extra
    // mandatory slot. It only counts as complete once the user has chosen at
    // least one channel — both flags off means "no preference set yet", so the
    // profile must not read 100%. These flags aren't on UserProfileType (they
    // come back on the raw /me payload), so read them defensively.
    const record = data as Record<string, unknown>;
    const contactPreferenceSet = !!record['notification_email'] || !!record['notification_sms'];

    const totalSlots = fields.length + 1;
    const completedSlots = completed.length + (contactPreferenceSet ? 1 : 0);
    return Math.round((completedSlots / totalSlots) * 100);
  });

  // ─── Refetch the active query ────────────────────────────────────
  public refetchProfile(): void {
    if (this.userProfileId() === 'own') {
      this.ownProfileQuery.refetch();
    } else {
      this.userByIdQuery.refetch();
    }
  }
}
